import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { SpeciesData } from 'src/app/data-models/catch-table-models/species-data.model';
import { TableRules } from 'src/app/data-models/catch-table-models/table-rules.model';
import { AddSpeciesDialogComponent } from 'src/app/dialogs/add-species-dialog/add-species-dialog.component';
import { CommonDataService } from '../storage/common-data.service';
import { sanitizeDecimal } from 'src/app/data-models/validator.model';

@Injectable({
  providedIn: 'root'
})

export class CatchService {

  catchColumns: string[] = ['speciesName', 'kept', 'released'];
  seineOffloadColumns: string[] = ['speciesName', 'weight', 'pieces'];
  gillNetOffloadColumns: string[] = ['speciesName', 'pieces'];
  private lastValidHoursValue: string = '';

  constructor(
    private dialog: MatDialog
  ) {

  }


  /*  ---------- Dialog Methods  ---------- */


  // add to, edit or delete from species catch list
  addSpeciesDialog(offload: boolean, fishListDetails: SpeciesData[], fishList: SpeciesData[], 
    tableRules: TableRules, table: MatTable<SpeciesData>, index: number = -1): Promise<boolean> {
 
    // editing or deleting a species
    if (index > -1) {
      return this.editSpecies(offload, fishListDetails, index, fishList, tableRules, table);
    }

    // adding a species
    else {
      return this.addSpecies(offload, fishListDetails, fishList, tableRules, table);
    }

  }

  // edit or delete a species from the catch
  editSpecies(offload: boolean, fishListDetails: SpeciesData[], index: number, 
    fishList: SpeciesData[], tableRules: TableRules, table: MatTable<SpeciesData>): Promise<boolean> {
 
    const dialogRef: MatDialogRef<AddSpeciesDialogComponent, any> = this.dialog.open(AddSpeciesDialogComponent, {
      width: '500px',
      height: '450px',
      data: { identifier: 'edit', species: fishListDetails[index], currentFishList: fishListDetails, 
      filteredFishList: fishList, tableInfo: tableRules }
    });

    return new Promise<boolean>((res, rej) => {
      dialogRef.afterClosed().subscribe((result: any) => {

        if (result) {
  
          // editing a species in the catch
          if (result !== 'delete') {
            fishListDetails[index] = this.getNewSpecies(offload, result);
          } else { // delete a species in the catch
            fishListDetails.splice(index, 1);
          }
  
          // update the table
          table.renderRows();
  
        }
        res(true);
      });
    })
    

  }

  // add new species to the catch
  addSpecies(offload: boolean, fishListDetails: SpeciesData[], fishList: SpeciesData[], 
    tableRules: TableRules, table: MatTable<SpeciesData>): Promise<boolean> {
 
    const dialogRef: MatDialogRef<AddSpeciesDialogComponent, any> = this.dialog.open(AddSpeciesDialogComponent, {
      width: '500px',
      height: '450px',
      data: { identifier: 'add', species: new SpeciesData(), currentFishList: fishListDetails, 
      filteredFishList: fishList, tableInfo: tableRules }
    });

    return new Promise<boolean>((res, rej) => {
      dialogRef.afterClosed().subscribe((result: any) => {
        if (result) {

          // adding a species to the catch
          if (result !== 'delete') {
            fishListDetails.push(this.getNewSpecies(offload, result));
          }

          // update the table
          table.renderRows();
        }
        res(true);
      });
    });
  }

  
  /*  ---------- Species Table Methods ---------- */


  // filters table data as well as species list accessible to the user
  filterTableData(unfilteredFishList: SpeciesData[], table: TableRules, 
    fishListDetails: SpeciesData[], fishList: SpeciesData[]): void {  

    for (let species of unfilteredFishList) {
      if (table.containsSpecies.includes(species.speciesName)) {
        fishListDetails.push(species);
      }
      if (!table.excludesSpecies.includes(species.speciesName)) {
        fishList.push(species);
      }
    }

  }

  // determines the cell styling for catch tables based on the data value and species type
  determineCellStyling(value: number, name: string = '', 
  table: TableRules = new TableRules()): string {

    if (value>0) {
      return 'bolded-cell';
    } else if (this.isReleaseOnly(name, table)) { 
      return 'release-only-cell';
    }
    return 'faded-cell';

  }
  
  // determines if a given species is release only for the input table
  isReleaseOnly(name: string, tableRules: TableRules): boolean {

    if (tableRules.releaseOnlySpecies.includes(name)){
      return true;
    }
    return false;

  }
  

  /*  ---------- Helper Methods ---------- */


  // creates a list of all species in the table that have non-zero data associated with them
  determineSpeciesData(fishListDetails: SpeciesData[], tableRules?: TableRules): SpeciesData[] {

    let tempFishArray: SpeciesData[] = [];

    for(let fish of fishListDetails) {
      if(fish.kept > 0 || fish.released > 0 || fish.offloadWeight > 0 || fish.offloadPieces > 0) {
        if (tableRules) {
          fish.catchCategory = tableRules.catchCategoryMappings.has(fish.speciesName) ? tableRules.catchCategoryMappings.get(fish.speciesName)! : 'O';
        }
        tempFishArray.push(fish);
      }
    }
  
    return tempFishArray;

  }

  // checks if a user has entered catch data for the given report (daily catch or offload)
  isCatchEmpty(report: string, fishListDetails: SpeciesData[]): boolean {

    let sum: number = 0;
    
    if (report === 'offload') {
      sum = this.calculateTotalFish('weight', fishListDetails) + 
            this.calculateTotalFish('pieces', fishListDetails);
    } else if (report === 'daily-catch') {
      sum = this.calculateTotalFish('kept', fishListDetails) + 
      this.calculateTotalFish('released', fishListDetails);
    }

    // true if sum is 0
    return !sum;

  }

  // sums the total amount of fish in the provided category (kept, released, etc.)
  calculateTotalFish(type: string, fishListDetails: SpeciesData[]): number {
   
    let totalFish: number = 0;

    switch (type) {

      case 'kept':
        for (let i: number = 0; i < fishListDetails.length; i++) {
          totalFish += fishListDetails[i].kept ?? 0;
        }
        break;
      case 'released':
        for (let i: number = 0; i < fishListDetails.length; i++) {
          totalFish += fishListDetails[i].released ?? 0;
        }
        break;
      case 'weight':
        for (let i: number = 0; i < fishListDetails.length; i++) {
          totalFish += fishListDetails[i].offloadWeight ?? 0;
        }
        break;
      case 'pieces':
        for (let i: number = 0; i < fishListDetails.length; i++) {
          totalFish += fishListDetails[i].offloadPieces ?? 0;
        }
        break;

    }

    return totalFish;
    
  }


  // accepts a species name and returns its corresponding code
  setSpeciesCodes(currSpecies: SpeciesData): void {

    let allSpecies: SpeciesData[] = CommonDataService.speciesList;
    for (let species of allSpecies) { // loop through the comprehensive species list
      if (species.speciesName === currSpecies.speciesName) { // if we get a match in names
        currSpecies.speciesCode = species.speciesCode;
        currSpecies.sizeCode = species.sizeCode;
        return; 
      }
    }

  }

  // get new species data from result
  getNewSpecies(offload: boolean, result: SpeciesData): SpeciesData {

    let newSpeciesData: SpeciesData = new SpeciesData(result.speciesName);
    this.setSpeciesCodes(newSpeciesData);
 
    if (offload) {
      let weight: number = result.offloadWeight ? +result.offloadWeight : 0;
      let pieces: number = result.offloadPieces ? +result.offloadPieces : 0;
      newSpeciesData.offloadWeight = weight;
      newSpeciesData.offloadPieces = pieces;
    } else {
      let kept: number = result.kept ? +result.kept : 0;
      let released: number = result.released ? +result.released : 0;
      newSpeciesData.kept = kept;
      newSpeciesData.released = released;
    }
   
    return newSpeciesData;

  }

  // only allow one decimal place with 5 as the tenth digit and prevent numeric input larger than 24
  validateHoursFished(controlName: string, form: UntypedFormGroup): void {

    const control: AbstractControl = form.get(controlName)!;

    // strip anything that isn't a digit or a decimal point and keep only the first point, so
    // fat-fingered input can no longer reach the report XML ('1..5' -> '1.5', '1,5' -> '15')
    let value: string = sanitizeDecimal(control.value, 1);

    // allow empty or a bare "." while typing
    if (value === '' || value === '.') {
      control.setValue(value, { emitEvent: false });
      this.lastValidHoursValue = '';
      return;
    }

    // normalize a leading decimal point ('.5' -> '0.5')
    if (value.startsWith('.')) {
      value = `0${value}`;
    }

    // snap any decimal to .5 if a decimal is present
    const parts: string[] = value.split('.');
    if (parts.length === 2 && parts[1] !== '') {
      value = `${parts[0]}.5`;
    }

    // prevent numeric input larger than 24 by reverting to the last accepted value
    if (parseFloat(value) > 24) {
      control.setValue(this.lastValidHoursValue, { emitEvent: false });
      return;
    }

    control.setValue(value, { emitEvent: false });

    // only remember complete values so a revert never restores a partial entry like '12.'
    if (!value.endsWith('.')) {
      this.lastValidHoursValue = value;
    }

  }

}
